import React, { useState } from "react";
import { CircleDot, Check, Sparkles, ArrowRight, ZoomIn } from "lucide-react";
import ImageLightbox from "./ImageLightbox";

// Bespoke per-case hero visual for case study detail page (larger, richer)
const CaseStudyHeroVisual = ({ cs }) => {
  const id = cs.id;
  const screens = cs.screens;

  return (
    <div className="relative">
      <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.18)] dark:shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/60 dark:bg-neutral-900/80">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-neutral-300 dark:bg-neutral-700" />
            <span className="w-2.5 h-2.5 rounded-full bg-neutral-300 dark:bg-neutral-700" />
            <span className="w-2.5 h-2.5 rounded-full bg-neutral-300 dark:bg-neutral-700" />
          </div>
          <div className="text-[11.5px] text-neutral-500 dark:text-neutral-400 font-mono">
            {id} · {screens ? "screens" : "hero"}
          </div>
          <span className="w-12" />
        </div>

        <div className="p-8 lg:p-12 bg-gradient-to-br from-white to-neutral-50/50 dark:from-neutral-900 dark:to-neutral-950 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(0,0,0,0.05)_1px,transparent_0)] dark:bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.06)_1px,transparent_0)] [background-size:22px_22px] opacity-70" />
          <div className="relative">
            {screens && <ScreensGrid screens={screens} aspect={cs.screenAspect} />}
            {!screens && id === "sparkminds" && <SparkMindsHero />}
            {!screens && id === "satvikscan" && <SatvikScanHero />}
            {!screens && id === "angelpro" && <AngelProHero />}
            {!screens && id === "qi-health" && <QiHealthHero />}
          </div>
        </div>
      </div>
    </div>
  );
};

// Real product screens grid — used when a case study has uploaded mockups
const GRID_COLS_BY_ASPECT = {
  laptop: "grid-cols-1 md:grid-cols-2",
  "phone-framed": "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  default: "grid-cols-2 lg:grid-cols-4",
};
const ASPECT_CLASS = {
  laptop: "aspect-[16/10]",
  "phone-framed": "aspect-square",
  phone: "aspect-[9/19]",
  default: "aspect-[9/19]",
};
const RADIUS_BY_ASPECT = {
  laptop: "rounded-2xl",
  default: "rounded-[28px]",
};
const FIT_BY_ASPECT = {
  laptop: "object-cover object-top",
  default: "object-contain",
};

const ScreensGrid = ({ screens, aspect }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const gridCols = GRID_COLS_BY_ASPECT[aspect] || GRID_COLS_BY_ASPECT.default;
  const defaultAspectClass = ASPECT_CLASS[aspect] || ASPECT_CLASS.default;
  const radiusClass = RADIUS_BY_ASPECT[aspect] || RADIUS_BY_ASPECT.default;
  const defaultFit = FIT_BY_ASPECT[aspect] || FIT_BY_ASPECT.default;

  // Per-screen aspect override resolver — if a screen declares its own
  // aspect (e.g. "phone-framed" on a "phone" grid), respect it.
  const resolveAspect = (override) =>
    ASPECT_CLASS[override] || defaultAspectClass;
  const resolveFit = (override) =>
    override && override !== aspect ? "object-contain" : defaultFit;

  return (
    <div>
      <div className={`grid ${gridCols} gap-5 lg:gap-6`}>
        {screens.map((s, i) => (
          <figure key={s.url || s.label || i} className="group relative flex flex-col">
            <button
              type="button"
              onClick={() => setOpenIndex(i)}
              aria-label={`Open ${s.label}`}
              className={`relative ${resolveAspect(s.aspect)} w-full ${radiusClass} overflow-hidden bg-neutral-100 dark:bg-neutral-950 border border-neutral-200/60 dark:border-neutral-800/60 transition-transform duration-500 ease-out group-hover:-translate-y-1 cursor-zoom-in`}
            >
              <img
                src={s.url}
                alt={s.label}
                loading="lazy"
                className={`absolute inset-0 w-full h-full ${resolveFit(s.aspect)}`}
              />
              {/* Hover overlay with zoom hint */}
              <span className="pointer-events-none absolute inset-0 bg-neutral-950/0 group-hover:bg-neutral-950/15 transition-colors duration-300 flex items-end justify-end p-3">
                <span className="opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/95 text-neutral-900 text-[11.5px] font-medium shadow-md">
                  <ZoomIn className="w-3 h-3" strokeWidth={2.25} />
                  View
                </span>
              </span>
            </button>
            <figcaption className="mt-4">
              <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-mono text-neutral-400 dark:text-neutral-500 tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[13.5px] font-medium text-neutral-900 dark:text-neutral-50">
                  {s.label}
                </span>
              </div>
              {s.caption && (
                <p className="mt-1 text-[12.5px] text-neutral-500 dark:text-neutral-400 leading-[1.5]">
                  {s.caption}
                </p>
              )}
            </figcaption>
          </figure>
        ))}
      </div>

      <ImageLightbox
        open={openIndex !== null}
        screens={screens}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onIndex={(n) => setOpenIndex(n)}
      />
    </div>
  );
};

const SparkMindsHero = () => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <div className="lg:col-span-7 space-y-3">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
        Conversational onboarding · step 4 of 11
      </div>
      <div className="p-5 rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800">
        <div className="text-[15px] font-medium text-neutral-900 dark:text-neutral-50">
          What does Aanya love doing right now?
        </div>
        <div className="mt-1 text-[12.5px] text-neutral-500 dark:text-neutral-400">
          Pick all that apply — you can change this anytime.
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {[
            { t: "Building things", on: true },
            { t: "Stories", on: true },
            { t: "Drawing", on: false },
            { t: "Music", on: false },
            { t: "Outdoor play", on: true },
            { t: "Number games", on: false },
          ].map((c) => (
            <div
              key={c.t}
              className={`px-3 py-2 rounded-lg text-[12.5px] border text-center ${
                c.on
                  ? "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 border-neutral-900 dark:border-white"
                  : "bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-800"
              }`}
            >
              {c.t}
            </div>
          ))}
        </div>
      </div>
      <div className="p-3.5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex items-start gap-3">
        <Sparkles className="w-4 h-4 mt-0.5 text-neutral-700 dark:text-neutral-300" strokeWidth={1.75} />
        <div className="text-[12.5px] text-neutral-700 dark:text-neutral-300 leading-snug">
          Aanya’s profile is <b>67%</b> personalised. 3 more questions and we’ll match her with her first activity.
        </div>
      </div>
    </div>
    <div className="lg:col-span-5 p-5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
        Aanya · 4y 7m
      </div>
      <div className="mt-4 space-y-3">
        {[
          { k: "Curiosity", v: 78 },
          { k: "Focus", v: 62 },
          { k: "Language", v: 84 },
          { k: "Motor", v: 71 },
          { k: "Empathy", v: 88 },
        ].map((s) => (
          <div key={s.k}>
            <div className="flex items-center justify-between text-[12px] text-neutral-600 dark:text-neutral-400">
              <span>{s.k}</span>
              <span className="font-mono text-neutral-500 dark:text-neutral-400">
                {s.v}
              </span>
            </div>
            <div className="mt-1 h-1 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
              <div
                className="h-full bg-neutral-900 dark:bg-neutral-200"
                style={{ width: `${s.v}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const SEVERITY_DOT_COLOR = {
  high: "bg-rose-500",
  med: "bg-amber-500",
  safe: "bg-emerald-500",
};

const SatvikScanHero = () => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
    <div className="lg:col-span-5 p-5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-medium text-neutral-900 dark:text-neutral-50">
          Cosmetic cream
        </div>
        <div className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30">
          Caution · 73%
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {[
          { k: "Parabens", s: "high" },
          { k: "Fragrance mix", s: "med" },
          { k: "Niacinamide", s: "safe" },
          { k: "Glycerin", s: "safe" },
          { k: "Phenoxyethanol", s: "med" },
        ].map((r) => (
          <div
            key={r.k}
            className="flex items-center justify-between text-[12.5px] py-1.5 border-b border-neutral-100 dark:border-neutral-800 last:border-b-0"
          >
            <span className="text-neutral-700 dark:text-neutral-300">{r.k}</span>
            <div className="flex items-center gap-1.5">
              <span
                className={`w-1.5 h-1.5 rounded-full ${SEVERITY_DOT_COLOR[r.s] || "bg-neutral-400"}`}
              />
              <span className="text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-[10px]">
                {r.s}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="lg:col-span-7 p-5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
        Why ‘Parabens’ was flagged
      </div>
      <div className="mt-3 text-[14px] text-neutral-800 dark:text-neutral-200 leading-[1.6]">
        Parabens are preservatives linked to mild endocrine disruption in long-term studies. Your profile flags them because you opted in to <b>hormone-sensitive</b> filters.
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {[
          { k: "Source", v: "3 studies" },
          { k: "Severity", v: "Medium" },
          { k: "Personal", v: "High" },
        ].map((c) => (
          <div
            key={c.k}
            className="p-3 rounded-lg border border-neutral-200 dark:border-neutral-800"
          >
            <div className="text-[10.5px] uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
              {c.k}
            </div>
            <div className="mt-1 text-[13px] text-neutral-800 dark:text-neutral-200">
              {c.v}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const AngelProHero = () => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <div className="lg:col-span-7 p-6 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
          Decision 2 of 3
        </div>
        <div className="text-[11px] text-neutral-500 dark:text-neutral-400">
          ~40s left
        </div>
      </div>
      <div className="mt-2 text-[18px] font-medium text-neutral-900 dark:text-neutral-50 tracking-tight">
        How long will you stay invested?
      </div>
      <div className="mt-1 text-[12.5px] text-neutral-500 dark:text-neutral-400">
        Helps us pick the right risk profile for you.
      </div>
      <div className="mt-5 grid grid-cols-3 gap-2">
        {[
          { t: "< 1 yr", s: "Short" },
          { t: "1–5 yrs", s: "Balanced" },
          { t: "5+ yrs", s: "Long" },
        ].map((t, i) => (
          <div
            key={t.t}
            className={`p-3 rounded-xl border text-center ${
              i === 1
                ? "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 border-neutral-900 dark:border-white"
                : "bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-800"
            }`}
          >
            <div className="text-[14px] font-medium">{t.t}</div>
            <div
              className={`text-[11px] mt-0.5 ${
                i === 1
                  ? "text-neutral-300 dark:text-neutral-500"
                  : "text-neutral-500 dark:text-neutral-500"
              }`}
            >
              {t.s}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 h-1 w-full rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
        <div className="h-full w-2/3 bg-neutral-900 dark:bg-neutral-200" />
      </div>
    </div>
    <div className="lg:col-span-5 p-6 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
        What we’d build for you
      </div>
      <div className="mt-4 space-y-3">
        {[
          { k: "Equities", v: "55%" },
          { k: "Bonds", v: "30%" },
          { k: "Index funds", v: "15%" },
        ].map((s) => (
          <div key={s.k}>
            <div className="flex items-center justify-between text-[12.5px] text-neutral-700 dark:text-neutral-300">
              <span>{s.k}</span>
              <span className="font-mono">{s.v}</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
              <div
                className="h-full bg-neutral-900 dark:bg-neutral-200"
                style={{ width: s.v }}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between text-[12px] text-neutral-500 dark:text-neutral-400">
        <span>Estimated 5y outcome</span>
        <span className="font-mono text-neutral-800 dark:text-neutral-200">+62% ± 9%</span>
      </div>
    </div>
  </div>
);

const QiHealthHero = () => (
  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <div className="lg:col-span-3 p-4 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
        Roles
      </div>
      <div className="mt-3 space-y-1">
        {[
          { r: "Doctor", a: true },
          { r: "Nurse", a: false },
          { r: "Admin", a: false },
          { r: "Patient", a: false },
        ].map((p) => (
          <div
            key={p.r}
            className={`px-2.5 py-1.5 rounded-md text-[13px] flex items-center justify-between ${
              p.a
                ? "bg-neutral-900 dark:bg-white text-white dark:text-neutral-900"
                : "text-neutral-700 dark:text-neutral-300"
            }`}
          >
            <span>{p.r}</span>
            {p.a && <Check className="w-3.5 h-3.5" strokeWidth={2.5} />}
          </div>
        ))}
      </div>
    </div>
    <div className="lg:col-span-9 p-5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      <div className="flex items-center justify-between">
        <div className="text-[14px] font-medium text-neutral-900 dark:text-neutral-50">
          Dr. Iyer · Today
        </div>
        <div className="flex items-center gap-2 text-[12px] text-neutral-500 dark:text-neutral-400">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          14 cases
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
        {[
          { t: "Critical", v: "3", d: "awaiting review" },
          { t: "Routine", v: "8", d: "in progress" },
          { t: "Discharge", v: "3", d: "ready today" },
        ].map((m) => (
          <div
            key={m.t}
            className="p-3 rounded-xl border border-neutral-200 dark:border-neutral-800"
          >
            <div className="text-[10.5px] uppercase tracking-widest text-neutral-400 dark:text-neutral-500">
              {m.t}
            </div>
            <div className="mt-1 text-[22px] font-medium tracking-tight text-neutral-900 dark:text-neutral-50">
              {m.v}
            </div>
            <div className="text-[11.5px] text-neutral-500 dark:text-neutral-400">
              {m.d}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 p-3 rounded-lg bg-neutral-50 dark:bg-neutral-950 border border-neutral-100 dark:border-neutral-800 flex items-center justify-between text-[12.5px]">
        <span className="text-neutral-700 dark:text-neutral-300">Patient view · same case</span>
        <span className="text-neutral-500 dark:text-neutral-400 inline-flex items-center gap-1">
          Open <ArrowRight className="w-3 h-3" strokeWidth={2.25} />
        </span>
      </div>
    </div>
  </div>
);

export default CaseStudyHeroVisual;
